import React, { Component } from 'react';
import { ActionMeta, GroupBase, InputActionMeta, MultiValue, SelectInstance, SingleValue } from 'react-select';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { CustomerDataFields } from '../../interfaces/CustomerData/CustomerData';
import { FormFieldFunctions } from '../../interfaces/FormField/FormField';
import { GlobalContext } from '../../interfaces/GlobalContext/GlobalContext';
import Option from '../../interfaces/Option/Option';
import Snapshot from '../../interfaces/Snapshot/Snapshot';
import { CustomDropdownIndicator, CustomInput } from '../FormFieldSelect/FormFieldSelectPartials';
import { formFieldSelectStylesForCustomerDataPage } from '../FormFieldSelect/FormFieldSelectStyles';
import FormFieldCreateUsernameProps from './FormFieldCreateUsername.props';
import FormFieldCreateUsernameState from './FormFieldCreateUsername.state';
import { CustomIndicatorSeparator } from './FormFieldCreateUsernamePartials';

export default class FormFieldCreateUsername extends Component<FormFieldCreateUsernameProps, FormFieldCreateUsernameState> implements FormFieldFunctions {
    static contextType = GlobalContext;
    context!: React.ContextType<typeof GlobalContext>;
    private block: React.RefObject<HTMLDivElement>;
    private select: React.RefObject<SelectInstance<Option, false, GroupBase<Option>>>;
    private validStatus: React.RefObject<HTMLDivElement>;

    constructor(props: FormFieldCreateUsernameProps) {
        super(props);
        const selectedPropValueIsInvalid = !!(this.props.maxLength && this.props.selected && this.props.selected.length > this.props.maxLength);
        this.state = {
            selected: selectedPropValueIsInvalid
                ? undefined
                : this.props.selected,
            disabled: selectedPropValueIsInvalid
                ? false
                : this.props.disabled || false,
            options: selectedPropValueIsInvalid
                ? []
                : this.props.selected
                    ? [{
                        label: this.props.selected,
                        value: this.props.selected
                    }]
                    : [],
            selectedIsValid: this.props.isLoggedIn && !selectedPropValueIsInvalid ? true : undefined,
        };

        this.block = React.createRef<HTMLDivElement>();
        this.select = React.createRef<SelectInstance<Option, false, GroupBase<Option>>>();
        this.validStatus = React.createRef<HTMLDivElement>();
        this.handleSelectChange = this.handleSelectChange.bind(this);
        this.getUsernameSuggestions = this.getUsernameSuggestions.bind(this);
        this.isUsernameAvailable = this.isUsernameAvailable.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.isValid = this.isValid.bind(this);
        this.toggleValidationError = this.toggleValidationError.bind(this);
        this.loadOptions = this.loadOptions.bind(this);
    }

    async handleSelectChange(newValue: MultiValue<Option> | SingleValue<Option>, actionMeta: ActionMeta<Option>) {
        if (!newValue) {
            return this.setState({
                selected: undefined,
                selectedIsValid: undefined,
            }, this.onBlur);
        }
        const isUsernameAvailable = !this.props.isLoggedIn && newValue
            ? await this.isUsernameAvailable((newValue as Option).value)
            : this.props.isLoggedIn === true
                ? true
                : undefined;
        this.setState({
            selected: (newValue as Option).value,
            selectedIsValid: isUsernameAvailable,
        }, this.onBlur);
    }

    updateAndNotify = async () => {
        const isUsernameAvailable = !this.props.isLoggedIn && this.props.selected
            ? await this.isUsernameAvailable(this.props.selected)
            : this.props.isLoggedIn === true
                ? true
                : undefined;
        this.setState({
            selected: this.props.selected,
            selectedIsValid: isUsernameAvailable,
            options: this.props.selected && isUsernameAvailable
                ? [{
                    label: this.props.selected,
                    value: this.props.selected
                }]
                : [],
        }, this.onBlur);
    }

    getSnapshotBeforeUpdate(prevProps: FormFieldCreateUsernameProps): Snapshot {
        return { 
            notifyRequired: prevProps.selected !== this.props.selected || prevProps.isLoggedIn !== this.props.isLoggedIn,
        };
    }

    componentDidUpdate(prevProps: FormFieldCreateUsernameProps, prevState: FormFieldCreateUsernameState, snapshot: Snapshot) {
        if (snapshot.notifyRequired) {
            this.updateAndNotify();
        }
    }

    onBlur() {
        if (this.isValid()) {
            this.toggleValidationError(false);
        }
    }

    isValid() {
        if (this.props.required && (this.state.selected === undefined || this.state.selected.trim() === '')) {
            return false;
        }

        if (this.state.selectedIsValid !== true) {
            return false;
        }

        if (this.props.maxLength && this.state.selected && this.state.selected.length > this.props.maxLength) {
            this.setState({
                selected: undefined,
                selectedIsValid: false
            });
            return false;
        }

        return true;
    }

    toggleValidationError(show: boolean) {
        if (show) {
            this.block.current?.parentElement?.classList.add('form__field--error');
        } else {
            this.block.current?.parentElement?.classList.remove('form__field--error');
        }
    }

    async isUsernameAvailable(login: string): Promise<boolean> {
        const response = await this.context.requestProvider.getData(
            `/order-api/testLogin?login=${login}`
        );
        return response.userExists === false;
    }

    async loadOptions(newValue: string): Promise<Option[]> {
        await new Promise(resolve => setTimeout(resolve, 1000));
        if (this.select.current
            && this.select.current.inputRef?.value === newValue) {
            const isUsernameAvailable = await this.isUsernameAvailable(newValue);
            // console.log(isUsernameAvailable);
            this.setState({
                selected: newValue,
                selectedIsValid: isUsernameAvailable,
                options: [
                    {
                        value: newValue,
                        label: newValue
                    },
                    ...this.state.options
                ]
            });
        }
        return [
            {
                value: newValue,
                label: newValue
            },
            ...this.state.options
        ];
    }

    async getUsernameSuggestions(e: React.FocusEvent<HTMLInputElement, Element>): Promise<Option[]> {
        if (this.isValid()
            || (!this.isValid()
                && (!this.props.formFieldName?.isValid()
                    || !this.props.formFieldFirstName?.isValid()
                    || !this.props.formFieldEmail?.isValid()))
            || this.state.options.length > 1) {
            return this.state.options;
        }

        const response = await this.context.requestProvider.postData(
            '/order-api/suggestUserName',
            {
                name: this.props.formFieldName?.getValue(),
                firstName: this.props.formFieldFirstName?.getValue(),
                email: this.props.formFieldEmail?.getValue(),
            }
        );

        if (response && response.status === 200) {
            const newSuggestionsList = (await response.json()) || [];
            const upgradedSuggestionsList = newSuggestionsList.map((suggestion: string) => {
                return {
                    label: suggestion,
                    value: suggestion
                } as Option
            }) as Option[];
            this.setState({
                options: [
                    ...this.state.options,
                    ...upgradedSuggestionsList
                ],
            });
            return upgradedSuggestionsList;
        }

        return [] as Option[];
    }

    getValue() {
        return this.state.selected;
    }

    render(): React.ReactNode {
        return (
            <div data-testid="FormFieldCreateUsername"
                className={
                    "select"
                    + (!!this.state.selected && this.state.selected !== 'default' ? " select--option-selected" : "")
                }
                ref={this.block}>
                <div className="select__inner">
                    <div className="select__content">
                        {
                            this.props.label &&
                            <label className="select__label" htmlFor={this.props.id}>{this.props.label}</label>
                        }
                        <AsyncCreatableSelect
                            maxLength={this.props.maxLength}
                            selectedIsValid={this.state.selectedIsValid}
                            isLoggedIn={this.props.isLoggedIn}
                            availableText={this.context.i18nProvider.get(`customerDataPage.customerDataFields.${CustomerDataFields.LOGIN}.available`)}
                            notAvailableText={this.context.i18nProvider.get(`customerDataPage.customerDataFields.${CustomerDataFields.LOGIN}.notAvailable`)}

                            inputId={this.props.id}
                            tabIndex={undefined}
                            tabSelectsValue={false}
                            name={this.props.name || this.props.id}
                            value={(this.state.selected && {
                                label: this.state.selected,
                                value: this.state.selected
                            } as Option) || undefined}
                            loadOptions={this.loadOptions}
                            defaultOptions={this.state.options}
                            onFocus={e => this.getUsernameSuggestions(e)}
                            isDisabled={this.state.disabled || (this.props.isLoggedIn 
                                && ((this.props.required && this.isValid()) || !this.props.required)) }
                            placeholder={this.props.label}
                            // defaultMenuIsOpen={true} // for debug purposes
                            closeMenuOnSelect={true}
                            isMulti={false}
                            onChange={this.handleSelectChange}
                            onBlur={this.onBlur}
                            onInputChange={(newValue: string, actionMeta: InputActionMeta) => {
                                // console.log(actionMeta)
                                if (actionMeta.action === 'input-change') {
                                    this.setState({
                                        selected: newValue
                                    });
                                }
                            }}
                            inputValue={this.state.selected}
                            styles={formFieldSelectStylesForCustomerDataPage}
                            formatCreateLabel={(inputValue: string) => inputValue}
                            ref={this.select}
                            noOptionsMessage={() => "Keine Optionen"}
                            components={{
                                DropdownIndicator: CustomDropdownIndicator,
                                IndicatorSeparator: CustomIndicatorSeparator,
                                Input: CustomInput
                            }} />
                    </div>
                </div>
            </div>
        );
    }
}
